import React, { useState, useRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { COUNTRIES } from '../shared/countries';
import { MapPinIcon, GlobeIcon, NumberIcon } from '../shared/Icons';

const ADDRESS_ELEMENT_ICONS: Partial<Record<AddressElementKey, React.FC<{ size?: number }>>> = {
  street: MapPinIcon,
  line2: MapPinIcon,
  city: MapPinIcon,
  state: MapPinIcon,
  postal: NumberIcon,
  country: GlobeIcon,
};

export type AddressElementKey =
  | 'street'
  | 'line2'
  | 'city'
  | 'state'
  | 'postal'
  | 'country';

export interface AddressElementConfig {
  key: AddressElementKey;
  label: string;
  visible: boolean;
  required: boolean;
  placeholder?: string;
}

export const DEFAULT_ADDRESS_ELEMENTS: AddressElementConfig[] = [
  {
    key: 'street',
    label: 'Street Address',
    visible: true,
    required: false,
    placeholder: 'Street address',
  },
  {
    key: 'line2',
    label: 'Address Line 2',
    visible: true,
    required: false,
    placeholder: 'Apartment, suite, unit',
  },
  { key: 'city', label: 'City', visible: true, required: false, placeholder: 'City' },
  {
    key: 'state',
    label: 'State/Region/Province',
    visible: true,
    required: false,
    placeholder: 'State / region',
  },
  {
    key: 'postal',
    label: 'Postal / Zip Code',
    visible: true,
    required: false,
    placeholder: 'Postal / zip code',
  },
  { key: 'country', label: 'Country', visible: true, required: false },
];

const FULL_WIDTH: AddressElementKey[] = ['street', 'line2'];

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .address-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 16px;
  }

  .address-cell.full {
    grid-column: 1 / -1;
  }

  .address-cell {
    min-width: 0;
  }

  .address-input-wrap {
    position: relative;
    display: block;
    width: 100%;
  }

  .address-input-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    color: #9ca3af;
    pointer-events: none;
    z-index: 1;
  }

  .address-input,
  .address-select {
    width: 100%;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    background: #fff;
    transition: border-color 0.15s ease;
  }

  .address-input.with-icon,
  .address-select.with-icon {
    padding-left: 38px;
  }

  .address-input:focus,
  .address-select:focus {
    border-color: #0069ff;
  }

  .address-caption {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-top: 4px;
    font-size: 0.8125rem;
    line-height: 1.4;
  }

  .address-caption .address-required {
    flex-shrink: 0;
    color: #d32f2f;
  }

  .address-cell.has-error .address-input,
  .address-cell.has-error .address-select {
    border-color: #d32f2f;
  }

  .address-error-text {
    margin-top: 3px;
    color: #d32f2f;
    font-size: 0.75rem;
  }

  @media (max-width: 600px) {
    .address-grid {
      grid-template-columns: 1fr;
    }
  }
`;

interface AddressInputData {
  field_name?: string;
  required?: boolean;
  addressElements?: AddressElementConfig[];
  showElementsLabel?: boolean;
  showIcon?: boolean;
  value?: Record<string, string>;
}

interface AddressInputProps {
  data: AddressInputData;
  onChange?: (value: Record<string, any>) => void;
  read_only?: boolean;
  mutable?: boolean;
}

const AddressInput = React.forwardRef<HTMLInputElement, AddressInputProps>(
  (props, ref) => {
    const {
      field_name,
      addressElements,
      showElementsLabel = true,
      showIcon = true,
      value: initialValue,
    } = props.data || {};

    const elements =
      Array.isArray(addressElements) && addressElements.length
        ? addressElements
        : DEFAULT_ADDRESS_ELEMENTS;

    const [values, setValues] = useState<Record<string, string>>(
      initialValue || {},
    );
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    const visible = elements.filter((el) => el.visible !== false);

    const toSubmitValue = (raw: Record<string, string>) => {
      const out: Record<string, { label: string; value: string }> = {};
      visible.forEach((el) => {
        const v = (raw[el.key] || '').trim();
        if (v) out[el.key] = { label: el.label, value: v };
      });
      return out;
    };

    const emitChange = (key: AddressElementKey, next: string) => {
      const nextValues = { ...values, [key]: next };
      setValues(nextValues);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: toSubmitValue(nextValues) });
      }
    };
    const isLiveForm = props.mutable !== false;

    return (
      <StyledWrapper>
        <div className="address-grid">
          {visible.map((el) => {
            const value = values[el.key] || '';
            const showError =
              isLiveForm && touched[el.key] && el.required && !value;
            const isFull = FULL_WIDTH.includes(el.key);

            return (
              <div
                key={el.key}
                className={`address-cell${isFull ? ' full' : ''}${
                  showError ? ' has-error' : ''
                }`}
              >
                {(() => {
                  const ElementIcon = ADDRESS_ELEMENT_ICONS[el.key];
                  const hasIcon = showIcon && ElementIcon;
                  return (
                    <div className="address-input-wrap ahd-icon-input-wrap">
                      {hasIcon && (
                        <span className="address-input-icon ahd-icon-input-icon">
                          <ElementIcon size={18} />
                        </span>
                      )}
                      {el.key === 'country' ? (
                        <select
                          className={`address-select${hasIcon ? ' with-icon' : ''}`}
                          name={field_name ? `${field_name}_${el.key}` : el.key}
                          value={value}
                          disabled={props.read_only}
                          onChange={(e) => emitChange(el.key, e.target.value)}
                          onBlur={() => setTouched((t) => ({ ...t, [el.key]: true }))}
                        >
                          <option value="">-Select-</option>
                          {COUNTRIES.map((country) => (
                            <option key={country} value={country}>
                              {country}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          className={`address-input${hasIcon ? ' with-icon' : ''}`}
                          type="text"
                          name={field_name ? `${field_name}_${el.key}` : el.key}
                          placeholder={el.placeholder || ''}
                          value={value}
                          disabled={props.read_only}
                          onChange={(e) => emitChange(el.key, e.target.value)}
                          onBlur={() => setTouched((t) => ({ ...t, [el.key]: true }))}
                        />
                      )}
                    </div>
                  );
                })()}

                {showElementsLabel && (
                  <div className="address-caption">
                    {el.label}
                    {el.required && <span className="address-required">*</span>}
                  </div>
                )}

                {showError && (
                  <div className="address-error-text">This field is required.</div>
                )}
              </div>
            );
          })}
        </div>

        <input
          ref={hiddenInputRef}
          type="hidden"
          name={field_name}
          data-value-json="true"
          value={JSON.stringify(toSubmitValue(values))}
        />
      </StyledWrapper>
    );
  },
);

AddressInput.displayName = 'AddressInput';

export default AddressInput;
